import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

import {
  BunnyPlayerTestMode,
  BunnyPlayerTestSkipMode,
  BunnyPlayerTestSettings,
  defaultBunnyPlayerTestSettings
} from '../models/bunny-player-test-settings.model';

@Injectable({ providedIn: 'root' })
export class BunnyPlayerTestSettingsService {
  private readonly sessionKey = 'aviator.video.bunnyPlayerTestSettings';
  private readonly settingsSubject = new BehaviorSubject<BunnyPlayerTestSettings>(this.load());

  readonly settings$: Observable<BunnyPlayerTestSettings> = this.settingsSubject.asObservable();

  get isConfigured(): boolean {
    return window.sessionStorage.getItem(this.sessionKey) !== null;
  }

  get current(): BunnyPlayerTestSettings {
    return { ...this.settingsSubject.value };
  }

  save(settings: BunnyPlayerTestSettings): void {
    const next = this.normalize(settings);
    window.sessionStorage.setItem(this.sessionKey, JSON.stringify(next));
    this.settingsSubject.next(next);
  }

  private load(): BunnyPlayerTestSettings {
    try {
      const value = window.sessionStorage.getItem(this.sessionKey);
      return value ? this.normalize(JSON.parse(value)) : { ...defaultBunnyPlayerTestSettings };
    } catch {
      return { ...defaultBunnyPlayerTestSettings };
    }
  }

  private normalize(settings: BunnyPlayerTestSettings): BunnyPlayerTestSettings {
    const supportedModes: BunnyPlayerTestMode[] = ['automatic', 'bunny', 'vidstack'];
    const supportedSkipModes: BunnyPlayerTestSkipMode[] = ['default', 'allow', 'prevent', 'completed'];
    const legacySettings = settings as BunnyPlayerTestSettings & { allowSkipping?: boolean };
    const legacyAllowSkipping = legacySettings?.allowSkipping;
    const legacyMode = (settings as any)?.mode as string;
    return {
      mode: legacyMode === 'vidstack-diagnostics'
        ? 'vidstack'
        : supportedModes.includes(settings?.mode) ? settings.mode : 'automatic',
      skipMode: supportedSkipModes.includes(settings?.skipMode)
        ? settings.skipMode
        : legacyAllowSkipping === true ? 'allow' : legacyAllowSkipping === false ? 'prevent' : 'default',
      showDiagnostics: settings?.showDiagnostics === true || legacyMode === 'vidstack-diagnostics'
    };
  }
}
